'use client';
import Link from 'next/link';
import { LanguageCode } from '@/lib/games';
import { translations } from '@/lib/translations';

interface FooterProps {
  lang: LanguageCode;
}

const Footer = ({ lang }: FooterProps) => {
  return (
    <footer className="bg-gray-800 text-white py-12">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* About Section */}
          <div className="space-y-4">
            <h3 className="text-xl font-bold gradient-text">
              {translations.footer.about.title[lang]}
            </h3>
            <p className="text-gray-300">
              {translations.footer.about.description[lang]}
            </p>
          </div>

          {/* Quick Links */}
          <div className="space-y-4">
            <h3 className="text-xl font-bold gradient-text">
              {translations.footer.quickLinks.title[lang]}
            </h3>
            <ul className="space-y-2">
              <li>
                <Link href="/" className="text-gray-300 hover:text-white transition-colors">
                  {translations.footer.quickLinks.home[lang]}
                </Link>
              </li>
              <li>
                <Link href="#gameArea" className="text-gray-300 hover:text-white transition-colors">
                  {translations.footer.quickLinks.playNow[lang]}
                </Link>
              </li>
              <li>
                <Link href="#tutorials" className="text-gray-300 hover:text-white transition-colors">
                  {translations.footer.quickLinks.tutorials[lang]}
                </Link>
              </li>
              <li>
                <Link href="#popularGames" className="text-gray-300 hover:text-white transition-colors">
                  {translations.footer.quickLinks.popularGames[lang]}
                </Link>
              </li>
            </ul>
          </div>

          {/* Community */}
          <div className="space-y-4">
            <h3 className="text-xl font-bold gradient-text">
              {translations.footer.community.title[lang]}
            </h3>
            <ul className="space-y-2">
              <li>
                <Link href="#" className="text-gray-300 hover:text-white transition-colors">
                  {translations.footer.community.discord[lang]}
                </Link>
              </li>
              <li>
                <Link href="#" className="text-gray-300 hover:text-white transition-colors">
                  {translations.footer.community.facebook[lang]}
                </Link>
              </li>
              <li>
                <Link href="#" className="text-gray-300 hover:text-white transition-colors">
                  {translations.footer.community.twitter[lang]}
                </Link>
              </li>
              <li>
                <Link href="#" className="text-gray-300 hover:text-white transition-colors">
                  {translations.footer.community.youtube[lang]}
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div className="space-y-4">
            <h3 className="text-xl font-bold gradient-text">
              {translations.footer.contact.title[lang]}
            </h3>
            <ul className="space-y-2">
              <li className="flex items-center text-gray-300">
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"></path>
                  <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"></path>
                </svg>
                {translations.footer.contact.email[lang]}
              </li>
              <li className="flex items-center text-gray-300">
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd"></path>
                </svg>
                {translations.footer.contact.address[lang]}
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Footer */}
        <div className="border-t border-gray-700 mt-8 pt-8 text-center">
          <p className="text-gray-400">
            &copy; {new Date().getFullYear()} {translations.footer.copyright[lang]}
          </p>
          <div className="mt-4 space-x-4">
            <Link href="#" className="text-gray-400 hover:text-white transition-colors">
              {translations.footer.links.privacy[lang]}
            </Link>
            <Link href="#" className="text-gray-400 hover:text-white transition-colors">
              {translations.footer.links.terms[lang]}
            </Link>
            <Link href="#" className="text-gray-400 hover:text-white transition-colors">
              {translations.footer.links.cookies[lang]}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;